import React, { ReactNode, useCallback } from "react";
import Layout from "../common/page/Layout";
import SearchSidebar from "./SearchSidebar";

// Search tab constants
export const SEARCH_TABS = {
  FILTERS: 'filters',
  LIST: 'list',
  DETAILS: 'details',
} as const;

export type SearchTab = typeof SEARCH_TABS[keyof typeof SEARCH_TABS];

export interface SearchPageLayoutProps {
  children: ReactNode;

  // Standard tab content - callers provide the content
  filtersContent?: ReactNode;
  listContent?: ReactNode;
  detailsContent?: ReactNode;

  // Tab configuration
  activeTab?: SearchTab;
  onTabChange?: (tab: SearchTab) => void;
  onBackToList?: () => void;
  stack?: SearchTab[];

  // Tab-specific props
  filtersTitle?: ReactNode;
  filtersFooter?: ReactNode;
  listTitle?: ReactNode;
  detailsTitle?: ReactNode;

  // Sidebar state
  isSidebarOpen?: boolean;
  onSidebarToggle?: (isOpen?: boolean) => void;

  // Tab visibility (optional - show/hide specific tabs)
  showFiltersTab?: boolean;
  showListTab?: boolean;
  showDetailsTab?: boolean;

  // Pass-through props for Layout
  sidebarOffsetHeight?: number;
  sidebarSize?: "sm" | "md" | "lg";
}

export default function SearchPageLayout({
  children,
  filtersContent,
  listContent,
  detailsContent,
  activeTab = SEARCH_TABS.FILTERS,
  onTabChange,
  onBackToList,
  stack,
  filtersTitle,
  filtersFooter,
  listTitle,
  detailsTitle,
  isSidebarOpen = false,
  onSidebarToggle,
  showFiltersTab = true,
  showListTab = true,
  showDetailsTab = true,
  sidebarOffsetHeight = 0,
  sidebarSize = "sm",
}: SearchPageLayoutProps) {

  // Auto-hide tabs that don't have content
  const effectiveShowFiltersTab = showFiltersTab && !!filtersContent;
  const effectiveShowListTab = showListTab && !!listContent;
  const effectiveShowDetailsTab = showDetailsTab && !!detailsContent;

  // Determine sidebar title based on active tab
  const getSidebarTitle = useCallback(() => {
    switch (activeTab) {
      case SEARCH_TABS.FILTERS:
        return filtersTitle || "Filters";
      case SEARCH_TABS.LIST:
        return listTitle;
      case SEARCH_TABS.DETAILS:
        return (
          <button
            onClick={() => onBackToList?.()}
            className="text-left hover:text-primary transition-colors cursor-pointer"
          >
            {detailsTitle || "Back to List"}
          </button>
        );
      default:
        return undefined;
    }
  }, [activeTab, filtersTitle, listTitle, detailsTitle, onBackToList]);

  return (
    <Layout
      isSidebarOpen={isSidebarOpen}
      onSidebarToggleClick={onSidebarToggle}
      SidebarProps={{
        offsetHeight: sidebarOffsetHeight,
        size: sidebarSize,
        title: getSidebarTitle(),
        footer: activeTab === SEARCH_TABS.FILTERS ? filtersFooter : undefined,
      }}
      sidebarContent={
        <SearchSidebar
          filtersContent={filtersContent}
          listContent={listContent}
          detailsContent={detailsContent}
          activeTab={activeTab}
          stack={stack}
          showFiltersTab={effectiveShowFiltersTab}
          showListTab={effectiveShowListTab}
          showDetailsTab={effectiveShowDetailsTab}
        />
      }
    >
      {children}
    </Layout>
  );
}